// 3rd-party modules
import { isMobile } from "react-device-detect";
import { message } from "antd";
import { useState } from "react";

// project modules
import Button from "../shared/button";
import Loader from "../shared/loader";
import { apiCall } from "../../helpers/apiHelper";

// apis
import * as PaymentApi from '../../apis/paymentApi';

// models
import { AccountKiboshDevice } from "../../models/accountKiboshDevice";
import { AccountLocation } from "../../models/accountLocation";
import { Product } from '../../models/product';
import { User } from "../../models/auth";

type Props = {
  account?: User;
  cancelText?: string;
  okText?: string;
  refresh?: boolean;
  selectedDevice?: AccountKiboshDevice | null;
  selectedBillingLocation?: AccountLocation | null;
  selectedProduct?: Product | null;
  onCancel?: () => void;
  onSave?: (amount: number) => void;
};

export default function AccountPayment({ account, cancelText = "Cancel", okText = "Save Changes", refresh, selectedDevice, selectedBillingLocation, selectedProduct, onCancel, onSave }: Props) {
  const [loading, setLoading] = useState(false);
  const abortController = new AbortController();

  const onCancelClick = () => {
    if (onCancel) {
      onCancel();
    }
  }

  const onCheckoutClick = async () => {
    if (!selectedDevice?.kiboshDeviceReference) {
      message.error("Something went wrong. Please go back to first step and select your device from the list");
      return;
    }

    const data = {
      priceId: selectedProduct?.priceId,
      mode: selectedProduct?.mode || 'subscription',
      email: account?.email,
      kiboshDeviceReference: selectedDevice?.kiboshDeviceReference,
      kiboshDeviceTypeCode: selectedDevice?.kiboshDeviceTypeCode
    };

    setLoading(true);

    if(isMobile){
      // Pre-open a blank tab synchronously (not blocked on iOS Safari)
      const newWindow = window.open('', '_blank');

      const response = await apiCall(PaymentApi.createCheckoutSession(data, abortController.signal));
      setLoading(false);

      if (response.success && newWindow) {
        newWindow.location.href = response.data?.value.sessionUrl;
        newWindow.focus();
      } else {
        // Optional: close the window if the request failed
        newWindow?.close();
        message.error("Could not create checkout session");
      }
    } else {
      const response = await apiCall(PaymentApi.createCheckoutSession(data, abortController.signal));
      setLoading(false);

      if (response.success) {
        const newWindow = window.open(response.data?.value.sessionUrl, '_blank');

        newWindow?.focus();
      }
    }
  };

  return (
    <>
      {loading && <Loader />}
      <form
        data-length="auto"
        data-type="group">
        <space data-height="40"></space>
        <group
          data-firection="column"
          data-align="center"
          data-justify="center"
          data-gap="20">
          <group
            data-elevation="5"
            data-radius="15"
            data-align="center"
            data-justify="center"
            data-direction="column"
            data-gap="5"
            data-space="20"
            data-background="main-background"
            data-shrink="0">
            <group data-direction="column">
              <group data-align="center" data-gap="5">
                <text data-wrap="wrap" data-weight="500" data-text-size="large">Ki<text data-weight="900" data-text-size="large">bosh</text></text>
                <text data-wrap="wrap" data-uppercase data-accent data-weight="900" data-text-size="large">Device</text>
              </group>
              <text data-weight="700" data-text-size="large" data-wrap="wrap">Name: {selectedDevice?.kiboshDeviceName}</text>
              <text data-weight="700" data-wrap="wrap">Reference: {selectedDevice?.kiboshDeviceReference}</text>
            </group>
            <group data-direction="column">
              <group data-align="center" data-gap="5">
                <text data-wrap="wrap" data-weight="500" data-text-size="large">Ki<text data-weight="900" data-text-size="large">bosh</text></text>
                <text data-wrap="wrap" data-uppercase data-accent data-weight="900" data-text-size="large">Billing Address</text>
              </group>
              <text data-weight="700" data-wrap="wrap">Address: {[selectedBillingLocation?.addressLine1, selectedBillingLocation?.addressLine2, selectedBillingLocation?.addressLine3, selectedBillingLocation?.city, selectedBillingLocation?.stateProvince, `${selectedBillingLocation?.country} ${selectedBillingLocation?.zipPostal}`].filter(x => x).join(", ")}</text>
            </group>
            <group data-direction="column">
              <group data-align="center" data-gap="5">
              <text data-wrap="wrap" data-weight="500" data-text-size="large">Ki<text data-weight="900" data-text-size="large">bosh</text></text>
                <text  data-wrap="wrap" data-uppercase data-accent data-weight="900" data-text-size="large">Product</text>
              </group>
              <text data-weight="700" data-wrap="wrap">Name: {selectedProduct?.name}</text>
              <text data-weight="700" data-text-size="x-large" data-wrap="wrap">Price: ${((selectedProduct?.price?.unitAmount || 0) / 100).toFixed(2)}</text>
            </group>
          </group>
        </group>
        <space data-height="40"></space>
        <group data-align="center" data-gap="10">
          <Button large material outline data-length="forcefit" onClick={() => onCancelClick()}>
            <text data-space-horizontal="10">Back</text>
          </Button>
          <Button large material primary data-length="forcefit" onClick={() => onCheckoutClick()}>
            <text>Checkout</text>
          </Button>
        </group>
        <space data-height="40" data-adaptive="mobile"></space>
      </form>
    </>
  );
}
